import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { useKeycloak } from "@react-keycloak/web";

import { 
  Plus, FolderOpen, MessagesSquare, ArrowLeft, LogOut,
  FileText,
} from 'lucide-react';
import LogoAiraStar from "../../../assets/img/logo-mjv-aira-star.svg";
import LogoMJVPowered from "../../../assets/img/logo-mjv-aira-powered.svg"
import LogoAiraWhite from "../../../assets/img/aira-white.svg";

import "./sidebar.css";
import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import TranslatorService from "../../../services/Translator/TranslatorService";
import PublishableService from "../../../services/Publishable/PublishableService";
import UserService from "../../../services/User/UserService";

const SideBar = () => {
  const [context, setContext] = useState(null);
  const [history, setHistory] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const { keycloak } = useKeycloak();
  const accessToken = keycloak.token;
  const logout = keycloak.logout;

  const sidebarRef = useRef(null);
  const toggleBtnRef = useRef(null);

  const openMenu = () => {
    if (sidebarRef.current) {
      sidebarRef.current.classList.toggle("expand");
      setIsExpanded((prev) => !prev);
    }
  };

  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const isChat = pathname.includes("/chat/");
    const newContext = isChat
      ? pathname.split("/chat/")[1].split("/")[0]
      : null;
    setContext(newContext);
  }, [pathname]);

  useEffect(() => {
    const fetchContext = async () => {
      
      const response = await fetch(
        `${process.env.REACT_APP_AUTH_API_URL}/chats/user`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const data = await response.json();
      setHistory(data || []);
    };
    fetchContext();
  }, [context, accessToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= 768) {
        if (
          sidebarRef.current &&
          !sidebarRef.current.contains(event.target) &&
          toggleBtnRef.current &&
          !toggleBtnRef.current.contains(event.target)
        ) {
          sidebarRef.current.classList.remove("expand");
        }
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    const fetchProjectsAndPublishables = async () => {
      if (!accessToken) return;
      const user = await UserService.getUserByToken(accessToken);
      try {
        setLoadingProjects(true);
        const publishables = await PublishableService.getPublishablesByUser(user.id, accessToken);

        const projectMap = publishables.reduce((map, pub) => {
          const { project, ...publishableData } = pub;
          if (!map.has(project.id)) {
            map.set(project.id, { id: project.id, name: project.name, publishables: [] });
          }
          map.get(project.id).publishables.push(publishableData);
          return map;
        }, new Map());

        const groupedProjects = Array.from(projectMap.values());
        setUserProjects(groupedProjects);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setUserProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjectsAndPublishables();
  }, [accessToken]);

  const handlePublishableClick = (pub) => {
    const signedUrl = pub.asset?.signed_url;
    if (!signedUrl) return;

    const isPdf =
      pub.name?.toLowerCase().endsWith(".pdf") ||
      signedUrl.toLowerCase().split("?")[0].endsWith(".pdf");

    if (isPdf) {
      navigate("/pdf-reader", {
        state: { pdfUrl: signedUrl, fileName: pub.name },
      });
    } else {
      const link = document.createElement("a");
      link.href = signedUrl;
      link.download = pub.name || "download";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <aside id="sidebar" className="expand" ref={sidebarRef}>
      <div className="d-flex justify-content-between mt-3">
        <Link to="/" className="sidebar-logo pl-3">
          <img src={LogoAiraWhite} alt="Logo Aira" style={{ width: "120px" }}/>
        </Link>
        <button
          className="toggle-btn"
          type="button"
          style={{ color: "white" }}
          onClick={openMenu}
          ref={toggleBtnRef}
        >
          {isExpanded ? <ArrowLeft /> : <img src={LogoAiraStar} alt="Logo Aira Star" />}
        </button>
        
      </div>
      <ul className="sidebar-nav">
      <li className="sidebar-item">
          <Link to="/" className="sidebar-link">
            <Plus />
            &ensp;
            <span>
              Novo chat
            </span>
          </Link>
        </li>
        <li className="sidebar-item">
          <button
            type="button"
            className="sidebar-link collapsed has-dropdown"
            data-bs-toggle="collapse"
            data-bs-target="#projects-dropdown"
            aria-expanded="false"
            aria-controls="projects-dropdown"
          >
            <FolderOpen />
            &ensp;
            <span>
              Projetos
            </span>
          </button>
          <ul
            id="projects-dropdown"
            className="sidebar-dropdown list-unstyled collapse"
            data-bs-parent="#sidebar"
          >
            {loadingProjects ? (
              <li className="sidebar-item">
                <span className="sidebar-link">Carregando...</span>
              </li>
            ) : userProjects.length > 0 ? (
              userProjects.map((project) => (
                <li key={project.id} className="sidebar-item">
                  <button
                    type="button"
                    className="sidebar-link collapsed has-dropdown"
                    data-bs-toggle="collapse"
                    data-bs-target={`#project-${project.id}`}
                    aria-expanded="false"
                    aria-controls={`project-${project.id}`}
                  >
                    &ensp;
                    <span>{project.name.length > 15 ? project.name.substring(0, 25) + '...' : project.name}</span>
                  </button>
                  <ul
                    id={`project-${project.id}`}
                    className="sidebar-dropdown list-unstyled collapse"
                    data-bs-parent="#projects-dropdown"
                  >
                    {project.publishables && project.publishables.length > 0 ? (
                      project.publishables.map((pub) => (
                        <li key={pub.id} className="sidebar-item">
                          <button
                            type="button"
                            className="sidebar-link"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePublishableClick(pub)}
                          >
                            <FileText />
                            &ensp;
                            {pub.name.length > 15 ? pub.name.substring(0, 25) + '...' : pub.name}
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="sidebar-item">
                        <span className="sidebar-link">
                          Nenhum entregável
                        </span>
                      </li>
                    )}
                  </ul>
                </li>
              ))
            ) : (
              <li className="sidebar-item">
                <span className="project-not-found">
                  Nenhum projeto encontrado
                </span>
              </li>
            )}
          </ul>
        </li>
        <li className="sidebar-item">
          <button
            type="button"
            className="sidebar-link collapsed has-dropdown"
            data-bs-toggle="collapse"
            data-bs-target="#history"
            aria-expanded="false"
            aria-controls="history"
          >
            <MessagesSquare />
            &ensp;
            <span>
              Chats
            </span>
          </button>
          <ul
            id="history"
            className="sidebar-dropdown list-unstyled collapse"
            data-bs-parent="#sidebar"
          >
            {history.length > 0 ? (
                history.slice(0,4).map((chat) => (
                  <li key={chat.id}>
                    <Link
                      to={`/chat/${chat.context?.projectId ?? chat.contextName}/${chat.id}`}
                      className="sidebar-link"
                    >
                      <FontAwesomeIcon icon={faFile} />
                      &ensp;{chat.title}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="sidebar-item">
                  <span className="sidebar-link">
                    <TranslatorService path="menu.historyNull" />
                  </span>
                </li>
              )
            }
            <li className="sidebar-item">
              <Link to={`/history-chats`} className="sidebar-link">
                Ver lista completa...
              </Link>
            </li>
          </ul>
        </li>
      </ul>
      <div className="sidebar-footer">
        <button type="button" onClick={handleLogout} className="sidebar-link">
          <LogOut />
          &ensp;
          <span>
            <TranslatorService path="menu.logout" />
          </span>
        </button>
      </div>
      <div className="sidebar-footer" style={{display:"flex",  justifyContent:"end", padding:"1rem"}}>
        {isExpanded && <img src={LogoMJVPowered} alt="Logo MJVPowered" />}
      </div>
    </aside>
  );
};

export default SideBar;
